import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import request from "../../../utilities/requests";
import useProfilePhoto from "../../../utilities/useProfilePhoto";
import ProfilePhotoBanner from "../../../components/ProfilePhotoBanner";

const ProfileHeader = ({ profile, refresh }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const inputFile = useRef(null);

  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState(profile?.firstName || "");
  const [lastName, setLastName] = useState(profile?.lastName || "");

  const {
    loading,
    previewUrl,
    syncToProfileImage,
    upload,
    defaultFocal,
  } = useProfilePhoto({ onUploaded: refresh, onFailed: refresh });

  const [focalX, setFocalX] = useState(profile?.photoFocalX ?? 50);
  const [focalY, setFocalY] = useState(profile?.photoFocalY ?? 30);

  useEffect(() => {
    if (profile) {
      setFirstName(profile?.firstName);
      setLastName(profile?.lastName);

      syncToProfileImage(profile?.imageUrl);

      const fx = profile?.photoFocalX ?? 50;
      const fy = profile?.photoFocalY ?? 30;
      setFocalX(fx);
      setFocalY(fy);
    }
  }, [profile, syncToProfileImage]);

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    // Reset focal point to default after new upload
    setFocalX(defaultFocal.x);
    setFocalY(defaultFocal.y);
    await upload(file);
  };

  const handleSaveName = async () => {
    const result = await request("profile/update", "post", { firstName, lastName });
    if (result.success) {
      refresh();
      setOpen(false);
    }
  };

  const photoUrl = previewUrl || profile?.imageUrl;
  const hasName = Boolean(profile?.firstName && profile?.lastName);

  return (
    <div style={{ width: "100%" }}>
      <ProfilePhotoBanner
        photoUrl={photoUrl}
        loading={loading}
        focalX={focalX}
        focalY={focalY}
        onSaved={(x, y) => {
          setFocalX(x);
          setFocalY(y);
        }}
        onPickPhoto={() => inputFile.current.click()}
        cameraBadgeProps={{ "data-tour": "profile-photo" }}
      />

      {/* Below-banner content */}
      <Grid
        container
        direction="column"
        spacing={2}
        justifyContent="center"
        alignItems="center"
        style={{ marginTop: 16 }}
      >
        {/* Name - a tappable chip that opens the edit dialog. Empty state shows
            a coral-bordered "Add your name" prompt instead of a red error. */}
        <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
          <div
            data-tour="profile-name"
            onClick={() => setOpen(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              border: `1.5px solid ${hasName ? "#efe6e0" : theme.palette.primary.main}`,
              background: "#fbf6f2",
              borderRadius: 12,
              padding: "9px 16px",
              cursor: "pointer",
            }}
          >
            <Typography
              variant="h5"
              style={{
                color: hasName ? undefined : "#a1968f",
                fontWeight: hasName ? undefined : 500,
              }}
            >
              {hasName
                ? `${profile.firstName} ${profile.lastName}`
                : t("profile.addname.label")}
            </Typography>
            <EditIcon style={{ color: theme.palette.primary.main, width: 20 }} />
          </div>
        </Grid>
      </Grid>

      <input
        type="file"
        id="file"
        ref={inputFile}
        style={{ display: "none" }}
        onChange={handleUpload}
      />

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{t("profile.editnamedialog.title")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label={t("profile.editfirstname.label")}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t("profile.editlastname.label")}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            {t("dialog.cancel.button")}
          </Button>
          <Button onClick={handleSaveName} color="primary">
            {t("dialog.save.button")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProfileHeader;
